
























import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import {
  Timeline,
  TimelineOptions,
  DataGroup,
  TimelineHiddenDateOption,
  DataSet,
} from "vis-timeline/standalone";

import { FocusedTask, TaskType, ScheduledTask } from "@/data/models/Task";
import {
  OccupiedTimeInfo,
  OccupiedTimeType,
} from "@/data/models/OccupiedTimeInfo";
import { MovingTaskData } from "@/data/models/MovingTaskData";
import { UserLookup } from "@/data/models/UserLookup";
import { TimelineConfiguration } from "@/data/models/TimelineConfiguration";
import { TimelineVisibleArea } from "@/data/models/TimelineSettings";
import TimelineSettingsComponent from "@/components/scheduling/settings/TimelineSettings.vue";
import TimelineControlComponent from "@/components/scheduling/timeline/TimelineControl.vue";
import {
  getTaskTypeColor,
  SchedulingSettings,
} from "@/data/models/SchedulingSettings";
import { SchedulingModule } from "@/store/modules/SchedulingModule";

import {
  TimlineDataItem,
  ItemType,
  TimelineItemDiffer,
} from "@/utils/scheduling/TimelineItemsDiffer";
import { DateTime } from "luxon";
import { COLOR_SCHEDULING_TIMLINE_VISIBLE_AREA_ONE_DAY, COLOR_SCHEDULING_TIMLINE_VISIBLE_AREA_OTHER } from "@/utils/colors";
import { getSchedulingScoreParts } from "@/utils/scheduling/SchedulingObjectiveFunction";
import { DEFAULT_LOCALE } from "@/plugins/vuetify";

@Component({
  components: {
    "timeline-settings": TimelineSettingsComponent,
    "timeline-control": TimelineControlComponent,
  },
})
export default class TimelineComponent extends Vue {
  componentActive = false;
  firstActivation = true;

  @Prop()
  scheduledTasks!: ScheduledTask[];

  @Prop()
  userLookup!: UserLookup;

  @Prop()
  focusedTask!: FocusedTask;

  @Prop()
  highlightedTaskId!: string;

  @Prop()
  groups!: DataGroup[];

  @Prop()
  timelineConfiguration!: TimelineConfiguration;

  @Prop()
  schedulingSettings!: SchedulingSettings;

  selectedCursor = "default";
  timeline!: Timeline;

  timelineItemDiffer = new TimelineItemDiffer();
  dataSet = new DataSet<TimlineDataItem>({ queue: { delay: 200 } });

  windowSizeChangedTrigger = 0;
  redrawTrigger = 0;

  movingTaskData: MovingTaskData = {
    id: "",
    startDate: new Date(),
    endDate: new Date(),
    userId: "",
  };

  @Watch("groups")
  onGroupsChanged(groups: DataGroup[]) {
    if (!this.timeline) {
      return;
    }

    this.timeline.setData({
      groups: groups,
    });
  }

  @Watch("redrawTrigger")
  onRedraw() {
    this.updateTimeline(this.scheduledTasks);
  }

  @Watch("scheduledTasks")
  onScheduledTasksChanged() {
    this.scheduleTimelineUpdate();
  }

  updateTimeline(scheduledTasks: ScheduledTask[]) {
    if (!this.timeline) {
      return;
    }

    if (!this.componentActive) {
      return;
    }

    const { start, end } = this.timelineConfiguration;

    const startTimestamp = start.getTime();
    const endTimestmap = end.getTime();
    const focusedTask = this.focusedTask;
    const focusedTaskId = focusedTask?.id;

    const displayedTaskIds = new Set<string>();

    const items: TimlineDataItem[] = scheduledTasks
      .filter(
        (scheduledTask) =>
          !(
            scheduledTask.endDate.getTime() < startTimestamp ||
            scheduledTask.startDate.getTime() > endTimestmap
          )
      )
      .map((scheduledTask) => {
        displayedTaskIds.add(scheduledTask.id);
        return this.createSchedueldTaskItem(scheduledTask, focusedTask);
      });

    this.getOccupiedTimeInfo(startTimestamp, endTimestmap).forEach(
      (occupiedTimeInfo) => {
        if (
          displayedTaskIds.has(occupiedTimeInfo.sourceTaskId) &&
          (!focusedTask?.isMoving ||
            (occupiedTimeInfo.fromTaskId != focusedTaskId &&
              occupiedTimeInfo.toTaskId != focusedTaskId))
        ) {
          const occupiedTimeItem = this.createOccupiedTimeItem(
            occupiedTimeInfo
          );
          items.push(occupiedTimeItem);
        }
      }
    );

    this.timelineItemDiffer.update(items, this.dataSet);

    if (focusedTask) {
      this.timeline.setSelection(focusedTask.id);
    }
  }

  createSchedueldTaskItem(
    scheduledTask: ScheduledTask,
    focusedTask: FocusedTask | undefined
  ) {
    let taskClass = "";

    switch (scheduledTask.taskType) {
      case TaskType.LEAK_DETECTION:
        taskClass = "task-leak-detection";
        break;
      case TaskType.DAMAGE_ASSESSMENT:
        taskClass = "task-damage-assessment";
        break;
      case TaskType.DRYING_SETUP:
        taskClass = "task-drying-setup";
        break;
      case TaskType.INTERMEDIATE_MEASUREMENT:
        taskClass = "task-intermediate-measurement";
        break;
      case TaskType.DRYING_CONCLUSION:
        taskClass = "task-drying-conclusion";
        break;
      case TaskType.REPAIR:
        taskClass = "task-drying-repair";
        break;
    }

    const isHighlightedTask = scheduledTask.id == this.highlightedTaskId;
    const isFocusedTask = scheduledTask.id == focusedTask?.id;
    const editable = isFocusedTask && (focusedTask?.editEnabled ?? false);

    const content = scheduledTask.projectNumber
      ? scheduledTask.projectNumber
      : scheduledTask.projectName;

    const startDate = scheduledTask.startDate;
    const endDate = scheduledTask.endDate;
    const userId = scheduledTask.userId;

    return {
      type: "range",
      className: `${
        isHighlightedTask
          ? scheduledTask.wasConfirmed
            ? "highlight-confirmed-task-item"
            : "highlight-task-item"
          : ""
      } ${taskClass} ${
        scheduledTask.wasConfirmed
          ? "confirmed-task-item"
          : "unconfirmed-task-item"
      }`,
      content: content,
      group: userId,
      id: scheduledTask.id,
      start: startDate,
      end: endDate,
      itemType: scheduledTask.wasConfirmed
        ? ItemType.CONFIRMED_TASK
        : ItemType.UNCONFIRMED_TASK,
      editable: editable,
      scheduledTask: scheduledTask,
      isMoving: isFocusedTask && focusedTask?.isMoving,
    };
  }

  createOccupiedTimeItem(occupiedTimeInfo: OccupiedTimeInfo) {
    return {
      content: occupiedTimeInfo.durationInMin.toString(),
      className: `occupied-time-item ${
        occupiedTimeInfo.type == OccupiedTimeType.TRAVEL_TIME
          ? "travel-time-item"
          : "break-time"
      }`,
      itemType:
        occupiedTimeInfo.type == OccupiedTimeType.TRAVEL_TIME
          ? ItemType.TRAVEL_TIME
          : ItemType.BREAK,

      start: new Date(occupiedTimeInfo.startTimestamp),
      end: new Date(occupiedTimeInfo.endTimestamp),

      id: occupiedTimeInfo.id,
      group: occupiedTimeInfo.userId,
      editable: false,
      isMoving: false,
    };
  }

  getOccupiedTimeInfo(
    startTimestamp: number,
    endTimestamp: number
  ): OccupiedTimeInfo[] {
    const start = DateTime.fromJSDate(new Date(startTimestamp))
      .startOf("day")
      .minus({ minute: 1 });

    const end = DateTime.fromJSDate(new Date(endTimestamp))
      .endOf("day")
      .plus({ minute: 1 });

    return SchedulingModule.tasksOfAllUsersLookup.getOccupiedTimeInfo(
      start.valueOf(),
      end.valueOf()
    );
  }

  @Watch("focusedTask")
  onFocusedTaskChanged(
    newFocusedTask: FocusedTask | null,
    oldFocusedTask: FocusedTask | null
  ) {
    const hasFocusedTaskChanged = newFocusedTask?.id !== oldFocusedTask?.id;
    const wasStartDateSet =
      newFocusedTask &&
      newFocusedTask.startDate !== undefined &&
      !oldFocusedTask?.startDate;

    if (hasFocusedTaskChanged || wasStartDateSet) {
      this.timeline.setSelection(newFocusedTask ? [newFocusedTask?.id] : []);
    }

    const didStartMoving =
      newFocusedTask?.isMoving && !oldFocusedTask?.isMoving;
    const didStopMoving = !newFocusedTask?.isMoving && oldFocusedTask?.isMoving;

    if (didStartMoving || didStopMoving) {
      this.scheduleTimelineUpdate();
    }
  }

  timelineUpdateDebouncer: NodeJS.Timeout | null = null;

  scheduleTimelineUpdate() {
    this.clearPanelUpdateDebouncer();

    this.timelineUpdateDebouncer = setTimeout(() => {
      this.updateTimeline(this.scheduledTasks);
    }, 100);
  }

  clearPanelUpdateDebouncer() {
    if (this.timelineUpdateDebouncer) {
      clearTimeout(this.timelineUpdateDebouncer);
    }
  }

  @Watch("timelineConfiguration")
  onTimelineConfigurationChanged(
    timelineConfiguration: TimelineConfiguration,
    previousTimelineConfiguration: TimelineConfiguration | undefined
  ) {
    if (!this.timeline) {
      return;
    }

    this.updateTimelineOptions(
      timelineConfiguration,
      previousTimelineConfiguration
    );

    this.updateTimeline(this.scheduledTasks);

    const focusedTask = this.focusedTask;
    this.timeline.setSelection(focusedTask ? [focusedTask.id] : []);
  }

  isMoving = false;

  getTimelineOptions(
    timelineConfiguration: TimelineConfiguration
  ): TimelineOptions {
    const {
      start: timelineStart,
      end: timelineEnd,
      hiddenDates,
    } = timelineConfiguration;

    const hiddenDateOptions: TimelineHiddenDateOption[] = [];

    hiddenDates.forEach((data) =>
      hiddenDateOptions.push({
        start: data.start,
        end: data.end,
        repeat: data.repeatDaily
          ? "daily"
          : data.repeatWeekly
          ? "weekly"
          : undefined,
      })
    );

    const {
      onlyShowWorkingHours,
      visibleArea,
    } = timelineConfiguration.timelineSettings;

    return {
      start: timelineStart,
      end: timelineEnd,
      showWeekScale: true,
      timeAxis: {
        scale: visibleArea === TimelineVisibleArea.ONE_DAY ? "hour" : "day",
      },

      showMinorLabels: true,
      showMajorLabels: false,

      showTooltips: true,
      tooltip: {
        delay: 0,
        followMouse: true,
        overflowMethod: "flip",
        template: (item) => {
          const focusedTask = this.focusedTask;

          if (!this.focusedTask || item.id !== focusedTask.id) {
            return "";
          }

          const score = focusedTask.schedulingScore;

          if (!score) {
            return "";
          }

          const scores = getSchedulingScoreParts(score);

          if (scores.length == 0) {
            return "";
          }

          return `<div>
                ${scores.map((line) => `<div>${line}</div>`).join("\n")}
                </div>`;
        },
      },

      stack: false,
      margin: {
        axis: 5,
      },
      moveable: false,
      zoomable: false,

      rollingMode: { follow: false },

      verticalScroll: true,
      maxHeight: this.getTimelineMaxHeight(),

      orientation: "top",
      itemsAlwaysDraggable: true,

      locale: DEFAULT_LOCALE,
      hiddenDates: hiddenDateOptions,
      xss: {
        disabled: false,
        filterOptions: {
          whiteList: { div: ["class"] },
        },
      },
      template: function(item) {
        let contentItemClass = "";

        switch (item.itemType) {
          case ItemType.UNCONFIRMED_TASK:
            contentItemClass = "unconfirmed-task-item-content";
            break;
          case ItemType.BREAK:
          case ItemType.TRAVEL_TIME:
            contentItemClass = "occupied-time-item-content";
            break;
        }

        return `<div class="${contentItemClass}">${item.content}</div>`;
      },
      format: {
        minorLabels: {
          hour: onlyShowWorkingHours ? "HH:mm" : "HH",
          day:
            visibleArea === TimelineVisibleArea.ONE_WEEK ||
            visibleArea === TimelineVisibleArea.TWO_WEEKS
              ? "dd D."
              : "D",
        },
        majorLabels: {
          day: "DD",
          month: "",
          year: "",
          week: "",
          weekday: "",
        },
      },
      groupHeightMode: "fixed",
      onInitialDrawComplete: () => {
        this.calculateLastMinorLabelTargetWidthTrigger++;
      },

      editable: {
        updateGroup: true,
        updateTime: true,
        remove: true,
        add: true,
      },

      snap: (date: Date) => {
        const snappedDate = this.isMoving
          ? this.snapDates(this.movingTaskData.startDate, date)
          : date;

        const minutes = snappedDate.getMinutes();
        const stepSizeInMinutes = 15;

        const roundedMinutes =
          Math.round(minutes / stepSizeInMinutes) * stepSizeInMinutes;

        snappedDate.setMinutes(roundedMinutes, 0, 0);

        return snappedDate;
      },
      onAdd: (item) => {
        this.$emit("addScheduledTask", item);
      },
      onMove: (item) => {
        this.isMoving = false;
        this.$emit("onTaskMoved", item);
      },
      onMoving: (item, callback) => {
        const { start, end, group } = item;

        if (end && group) {
          if (this.isRoleGroup(group)) {
            return;
          }

          const movingTaskData = this.movingTaskData;
          const startDate = new Date(start);

          this.isMoving = true;

          movingTaskData.id = item.id.toString();
          movingTaskData.startDate = startDate;
          movingTaskData.endDate = new Date(end);
          movingTaskData.userId = group.toString();

          callback(item);

          this.$emit("onTaskMoving", movingTaskData);
        }
      },
      onRemove: (item) => {
        this.$emit("removeScheduledTask", item.id);
      },
    };
  }

  snapDates(oldDate: Date, newDate: Date): Date {
    const {
      visibleAreaOfDayStart,
      visibleAreaOfDayEnd,
    } = this.timelineConfiguration;

    if (
      this.timelineConfiguration.timelineSettings.visibleArea !==
      TimelineVisibleArea.ONE_DAY
    ) {
      const minMinutes =
        visibleAreaOfDayStart.getHours() * 60 +
        visibleAreaOfDayStart.getMinutes();
      const maxMinutes =
        visibleAreaOfDayEnd.getHours() * 60 + visibleAreaOfDayEnd.getMinutes();
      const newMinutes = newDate.getHours() * 60 + newDate.getMinutes();

      if (newMinutes <= minMinutes) {
        newDate.setDate(newDate.getDate() - 1);
        newDate.setHours(visibleAreaOfDayEnd.getHours());
        newDate.setMinutes(visibleAreaOfDayEnd.getMinutes());
        newDate.setSeconds(0);
        newDate.setMilliseconds(0);
      } else if (newMinutes >= maxMinutes) {
        newDate.setDate(newDate.getDate() + 1);
        newDate.setHours(visibleAreaOfDayStart.getHours());
        newDate.setMinutes(visibleAreaOfDayStart.getMinutes());
        newDate.setSeconds(0);
        newDate.setMilliseconds(0);
      }
    }

    const {
      start: timelineStart,
      end: timelineEnd,
    } = this.timelineConfiguration;

    if (newDate.getTime() < timelineStart.getTime()) {
      return timelineStart;
    }

    if (newDate.getTime() > timelineEnd.getTime()) {
      return timelineEnd;
    }

    return newDate;
  }

  updateTimelineOptions(
    timelineConfiguration: TimelineConfiguration,
    previousTimelineConfiguration?: TimelineConfiguration
  ) {
    if (!this.componentActive) {
      return;
    }

    this.timeline.setOptions(this.getTimelineOptions(timelineConfiguration));

    this.addCustomTimes(
      previousTimelineConfiguration?.highlightHours,
      timelineConfiguration.highlightHours,
      false
    );

    this.addCustomTimes(
      previousTimelineConfiguration?.highlightDates,
      timelineConfiguration.highlightDates,
      true
    );
  }

  addCustomTimes(
    previousCustomTimes: Date[] | undefined,
    newCustomTimes: Date[],
    isDateHighlight: boolean
  ) {
    previousCustomTimes?.forEach((date) => {
      try {
        this.timeline.removeCustomTime(
          this.createCustomTimeId(date, isDateHighlight)
        );
      } catch (e) {
        e;
      }
    });

    newCustomTimes.forEach((date) => {
      this.timeline.addCustomTime(
        date,
        this.createCustomTimeId(date, isDateHighlight)
      );
    });
  }

  activated() {
    this.componentActive = true;

    if (!this.firstActivation) {
      this.redrawTrigger++;
    }

    this.firstActivation = false;
  }

  deactivated() {
    this.componentActive = false;
  }

  createCustomTimeId(date: Date, isDateHighlight: boolean) {
    const className = isDateHighlight ? "highlight_date" : "highlight_hour";
    return `${className}_${date.toISOString()}`;
  }

  roleGroupPrefix = "_role_";

  isRoleGroup(id: string | number | null | undefined) {
    return id && id.toString().startsWith(this.roleGroupPrefix);
  }

  checkForCursorUpdate(newCursor: string) {
    if (this.selectedCursor !== newCursor) {
      this.selectedCursor = newCursor;
    }
  }

  getTimelineMaxHeight() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const timelineContent: any = this.$refs["timelineContentRef"];
    const clientHeight = timelineContent?.clientHeight;
    return clientHeight ? `${clientHeight}px` : undefined;
  }

  calculateLastMinorLabelTargetWidthTrigger = 0;
  lastMinorLabelTargetWidth = "100%";

  onWindowResize() {
    if (!this.timeline) {
      return;
    }

    if (!this.componentActive) {
      return;
    }

    this.timeline.setOptions({
      maxHeight: this.getTimelineMaxHeight(),
    });

    this.calculateLastMinorLabelTargetWidthTrigger++;
  }

  onDatasetChanged() {
    const focusedTask = this.focusedTask;
    if (focusedTask) {
      this.timeline.setSelection(focusedTask.id);
    }
  }

  unmounted() {
    this.dataSet.off("*", this.onDatasetChanged);
  }

  mounted() {
    this.dataSet.on("*", this.onDatasetChanged);

    const container = document.getElementById("timelineContainer");

    if (container) {
      this.timeline = new Timeline(
        container,
        this.dataSet,
        this.groups,
        this.getTimelineOptions(this.timelineConfiguration)
      );

      this.updateTimeline(this.scheduledTasks);

      container.addEventListener(
        "click",
        (event) => {
          const eventProperties = this.timeline.getEventProperties(event);
          const { item: taskId } = eventProperties;

          //Prevent deselection of focused task when clicking on background
          if (eventProperties.what == "background") {
            const focusedTask = this.focusedTask;
            if (focusedTask && this.timeline.getSelection().length == 0) {
              this.timeline.setSelection(focusedTask.id);
            }
          } else if (eventProperties.what == "axis") {
            //Focus on date if not already showing single day
            if (
              this.timelineConfiguration.timelineSettings.visibleArea !==
              TimelineVisibleArea.ONE_DAY
            ) {
              SchedulingModule.setTimelineSettings({
                ...SchedulingModule.timelineSettings,
                startDate: eventProperties.snappedTime,
                visibleArea: TimelineVisibleArea.ONE_DAY,
              });
            }
          }

          if (taskId) {
            this.$emit("taskSelected", taskId);
          }
        },
        { passive: true }
      );
    }
  }

  get cssProps() {
    const schedulingSettings = this.schedulingSettings;
    const {
      visibleArea,
      onlyShowWorkingHours,
    } = this.timelineConfiguration.timelineSettings;

    const alignment =
      visibleArea == TimelineVisibleArea.ONE_WEEK && onlyShowWorkingHours
        ? "left"
        : "center";

    const planningBoardSettings = schedulingSettings.planningBoardSettings;

    return {
      "--last-minor-label-alignment": alignment,
      "--time-axis-cursor":
        visibleArea == TimelineVisibleArea.ONE_DAY ? "default" : "pointer",
      "--today-background-color":
        visibleArea == TimelineVisibleArea.ONE_DAY
          ? COLOR_SCHEDULING_TIMLINE_VISIBLE_AREA_ONE_DAY
          : COLOR_SCHEDULING_TIMLINE_VISIBLE_AREA_OTHER,

      "--leak-detection-color": getTaskTypeColor(
        TaskType.LEAK_DETECTION,
        planningBoardSettings
      ),
      "--damage-assessment-color": getTaskTypeColor(
        TaskType.DAMAGE_ASSESSMENT,
        planningBoardSettings
      ),
      "--drying-setup-color": getTaskTypeColor(
        TaskType.DRYING_SETUP,
        planningBoardSettings
      ),
      "--intermediate-measurement-color": getTaskTypeColor(
        TaskType.INTERMEDIATE_MEASUREMENT,
        planningBoardSettings
      ),
      "--drying-conclusion-color": getTaskTypeColor(
        TaskType.DRYING_CONCLUSION,
        planningBoardSettings
      ),
      "--repair-color": getTaskTypeColor(
        TaskType.REPAIR,
        planningBoardSettings
      ),
    };
  }
}
